import { Person, PersonOff } from '@mui/icons-material';
import { Box } from '@mui/material';

import { UNASSIGNED_QHP_ID } from '../../constants/visit';
import { QhpSimple } from '../../fetching/sharedSchemas/basicSchemas';
import { InputState, useTransformedInputState } from '../../utils/inputState';
import { ReturnComponent } from '../../utils/reactTypes';
import { AxleAutocompleteField } from './AxleAutocompleteField';

// Renders the logo or fallback icon of a QHP or the "Unassigned" option.
export const ClinicianLabel = ({
    option,
}: {
    option: { id: string; firstName: string; lastName: string; logoUrl: string | null };
}): ReturnComponent => {
    if (option.id === UNASSIGNED_QHP_ID) {
        return (
            <Box display='flex'>
                <PersonOff /> <Box ml={1}>Unassigned</Box>
            </Box>
        );
    }
    const name = option.firstName + (option.lastName ? ' ' + option.lastName : '');
    return (
        <Box display='flex'>
            {option.logoUrl ? (
                <Box
                    component='img'
                    src={option.logoUrl}
                    alt={name}
                    style={{
                        objectFit: 'contain',
                        width: '1.5em',
                        height: '1.5em',
                    }}
                />
            ) : (
                <Person />
            )}{' '}
            <Box ml={1}>{name}</Box>
        </Box>
    );
};

export const QhpLabel = ({
    option,
}: {
    option: { uuid: string; name: string; logo_url: string | null };
}): ReturnComponent => {
    if (option.uuid === UNASSIGNED_QHP_ID) {
        return (
            <Box display='flex'>
                <PersonOff /> <Box ml={1}>Unassigned</Box>
            </Box>
        );
    }
    return (
        <Box display='flex'>
            {option.logo_url ? (
                <Box
                    component='img'
                    src={option.logo_url}
                    alt={option.name}
                    style={{
                        objectFit: 'contain',
                        width: '1.5em',
                        height: '1.5em',
                    }}
                />
            ) : (
                <Person />
            )}{' '}
            <Box ml={1}>{option.name}</Box>
        </Box>
    );
};

export const ClinicianSelect = ({
    label,
    inputState,
    enabled = true,
    clinicians,
}: {
    label: string;
    inputState: InputState<string | null>;
    enabled?: boolean;
    clinicians: { id: string; fullName: string }[];
}): ReturnComponent => {
    return (
        <AxleAutocompleteField
            label={label}
            enabled={enabled}
            inputState={inputState}
            options={clinicians}
            optionToValue={(clinician) => clinician.id}
            optionToLabel={(clinician) => clinician.fullName}
        />
    );
};

export const QhpSelect = <QhpType extends QhpSimple>({
    label,
    inputState,
    enabled = true,
    qhps,
}: {
    label: string;
    inputState: InputState<QhpType | null>;
    enabled?: boolean;
    qhps: QhpType[];
}): ReturnComponent => {
    const qhpIdInputState: InputState<string> = useTransformedInputState({
        inputState,
        transform: (qhp) => qhp?.uuid ?? '',
        untransform: (qhpId) => qhps.find((qhp) => qhp.uuid === qhpId) ?? null,
    });

    return (
        <AxleAutocompleteField
            label={label}
            enabled={enabled}
            inputState={qhpIdInputState}
            options={qhps}
            optionToValue={(qhp) => qhp.uuid}
            optionToLabel={(qhp) => qhp.name}
        />
    );
};
